import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Button, Tabs, Tab, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from '../components/common/PageHeader';
import SearchBar from '../components/common/SearchBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ConfirmDialog from '../components/common/ConfirmDialog';
import UserTable from '../components/users/UserTable';
import { getUsers, toggleUserStatus } from '../services/userService';
import { ROLES, ROLE_LABELS } from '../config/constants';

const ROLE_TABS = [
  { label: 'All', value: '' },
  { label: ROLE_LABELS[ROLES.ADMIN], value: ROLES.ADMIN },
  { label: ROLE_LABELS[ROLES.MANAGER], value: ROLES.MANAGER },
  { label: ROLE_LABELS[ROLES.ASSOCIATE], value: ROLES.ASSOCIATE },
  { label: ROLE_LABELS[ROLES.BANKER], value: ROLES.BANKER },
  { label: ROLE_LABELS[ROLES.CONSULTANCY], value: ROLES.CONSULTANCY },
];

const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, user: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const { data } = await getUsers(params);
      setUsers(data.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user) => {
    navigate(`/users/${user._id || user.id}/edit`);
  };

  const handleDeactivateClick = (user) => {
    setConfirmDialog({
      open: true,
      user,
    });
  };

  const handleDeactivateConfirm = async () => {
    const { user } = confirmDialog;
    if (!user) return;
    try {
      const { data } = await toggleUserStatus(user._id || user.id);
      const newStatus = data.data?.user?.isActive;
      setSnackbar({
        open: true,
        message: `User ${newStatus ? 'activated' : 'deactivated'} successfully`,
        severity: 'success',
      });
      fetchUsers();
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to update user status';
      setSnackbar({ open: true, message: errMsg, severity: 'error' });
    } finally {
      setConfirmDialog({ open: false, user: null });
    }
  };

  const handleRoleTabChange = (_, newValue) => {
    setRoleFilter(ROLE_TABS[newValue].value);
  };

  const currentTabIndex = ROLE_TABS.findIndex((t) => t.value === roleFilter);

  return (
    <Box>
      <PageHeader title="User Management">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/new')}
        >
          Add User
        </Button>
      </PageHeader>

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTabIndex >= 0 ? currentTabIndex : 0}
          onChange={handleRoleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {ROLE_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by name, email, phone..."
        />
      </Paper>

      {/* Users table */}
      {loading && users.length === 0 ? (
        <LoadingSpinner />
      ) : (
        <Paper sx={{ p: 2 }}>
          <UserTable
            users={users}
            loading={loading}
            onEdit={handleEdit}
            onDeactivate={handleDeactivateClick}
          />
        </Paper>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.user?.isActive ? 'Deactivate User' : 'Activate User'}
        message={`Are you sure you want to ${confirmDialog.user?.isActive ? 'deactivate' : 'activate'} ${confirmDialog.user?.name || 'this user'}?`}
        onConfirm={handleDeactivateConfirm}
        onCancel={() => setConfirmDialog({ open: false, user: null })}
        confirmText={confirmDialog.user?.isActive ? 'Deactivate' : 'Activate'}
        confirmColor={confirmDialog.user?.isActive ? 'error' : 'success'}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
